import { isAccommodation } from "@@/shared/acquire/is-accommodation";
import { isBookable } from "@@/shared/acquire/is-bookable";
import { isBuyable } from "@@/shared/acquire/is-buyable";
import { isGiftCard } from "@@/shared/acquire/is-giftcard";
import { isVoucher } from "@@/shared/acquire/is-voucher";
import { exists } from "@@/utilities/exists";
import { hasProp } from "@@/utilities/has-prop";
import { filter, map, pipe, unique } from "remeda";
import type { AcquireType } from "../acquire/acquirable";
import { acquireTypes } from "../acquire/acquirable";
import type { DateRange } from "../date-time/date-range";
import { dateRangeFactory } from "../date-time/date-range";
import type { TimeRange } from "../date-time/time-range";
import { timeRangeFactory, timeRangeUtils } from "../date-time/time-range";
import type { ProductId } from "../products/product-id";
import type { RequiredTranslatable } from "../translations/translation";
import type { Order } from "./order";
import {
    isOrderItemAccommodation,
    isOrderItemBookable,
    isOrderItemBuyable,
    isOrderItemGiftCard,
    isOrderItemVoucher,
} from "./order-items/is-order-item";
import type {
    OrderItem,
    OrderItemAccommodation,
    OrderItemBookable,
    OrderItemBuyable,
    OrderItemGiftCard,
    OrderItemVoucher,
} from "./order-items/order-item";

type MainProductOrderItemType =
    | OrderItemBookable
    | OrderItemGiftCard
    | OrderItemAccommodation
    | OrderItemVoucher
    | OrderItemBuyable;
const getMainProductOrderItem = (
    order: Order,
): MainProductOrderItemType | undefined => {
    switch (order.type) {
        case "MIXED":
        case "BOOKABLE":
            return order.orderItems.find(item => isBookable(item));
        case "GIFTCARD":
            return order.orderItems.find(item => isGiftCard(item));
        case "ACCOMMODATION":
            return order.orderItems.find(item => isAccommodation(item));
        case "VOUCHER":
            return order.orderItems.find(item => isVoucher(item));
        case "BUYABLE":
        default:
            return order.orderItems.find(item => isBuyable(item));
    }
};

const getMainProductId = (order: Order): ProductId | undefined => {
    return getMainProductOrderItem(order)?.productId;
};
const getMainProductTitle = (
    order: Order,
): RequiredTranslatable | undefined => {
    return getMainProductOrderItem(order)?.meta.title;
};

function getProductIds(
    orderItems: OrderItem[],
    /** defaults to `["BOOKABLE", "BUYABLE", "VOUCHER", "GIFTCARD", "ACCOMMODATION"]` */
    forProductTypes?: AcquireType[],
): ProductId[];
function getProductIds(order: Order, acquireTypes?: AcquireType[]): ProductId[];
function getProductIds(
    params: Order | OrderItem[],
    /** defaults to `["BOOKABLE", "BUYABLE", "VOUCHER", "GIFTCARD", "ACCOMMODATION"]` */
    forProductTypes?: AcquireType[],
): ProductId[];
function getProductIds(
    params: Order | OrderItem[],
    /** defaults to `["BOOKABLE", "BUYABLE", "VOUCHER", "GIFTCARD", "ACCOMMODATION"]` */
    forProductTypes: AcquireType[] = [...acquireTypes],
): ProductId[] {
    const orderItems = Array.isArray(params) ? params : params.orderItems;
    const acquireTypeSet = new Set(forProductTypes);
    return pipe(
        orderItems,
        map(item => {
            // if the requester has specified to match no acquire types
            // we don't need to do any more checking since none will match
            if (!acquireTypeSet.size) return null;
            // if the item's acquire type is not part of the requested types
            // we don't have a match
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (!acquireTypeSet.has(item.acquireType as any)) return null;

            // if the item has a productId, we can return it
            if (hasProp(item, "productId")) {
                return item.productId;
            }

            // else we just return null since
            // there is no productId to return
            return null;
        }),
        filter(exists),
        // Let's make sure we don't have duplicates
        // duplicate product ids are unnecessary
        unique(),
    );
}

const getOrderTimeRange = (order: Order, doesNotThrow = false): TimeRange => {
    if (isAccommodation(order)) {
        const items = order.orderItems.filter(isOrderItemAccommodation);
        if (!items.length)
            throw new Error(
                "No accommodation items found in accommodation order",
            );
        const timeRange = timeRangeUtils.mergeTimeRanges(
            items.map(item => item.time),
        );
        return timeRange;
    }
    if (isBookable(order)) {
        const items = order.orderItems.filter(isOrderItemBookable);
        if (!items.length)
            throw new Error("No bookable items found in bookable order");
        const timeRange = timeRangeUtils.mergeTimeRanges(
            items.map(item => item.time),
        );
        return timeRange;
    }
    if (isGiftCard(order)) {
        const items = order.orderItems.filter(isOrderItemGiftCard);
        if (!items.length)
            throw new Error("No gift card items found in gift card order");
        return timeRangeFactory(
            order.delivery.estimatedDeliveryTime ?? order._createdAt,
        );
    }
    if (isVoucher(order)) {
        const items = order.orderItems.filter(isOrderItemVoucher);
        if (!items.length)
            throw new Error("No voucher items found in voucher order");
        return timeRangeFactory(
            order.delivery.estimatedDeliveryTime ?? order._createdAt,
        );
    }
    if (isBuyable(order)) {
        const items = order.orderItems.filter(isOrderItemBuyable);
        if (!items.length)
            throw new Error("No buyable items found in buyable order");
        return timeRangeFactory(
            order.delivery.estimatedDeliveryTime ?? order._createdAt,
        );
    }
    if (doesNotThrow) {
        return timeRangeFactory(order.fulfillmentDate);
    }
    throw new Error("Unknown order type");
};
const getOrderDateRange = (order: Order): DateRange => {
    return dateRangeFactory.fromTimeRange({
        timeRange: getOrderTimeRange(order),
    });
};

const orderUtils = {
    getMainProductTitle,
    getMainProductId,
    getMainProductOrderItem,
    getProductIds,
    getOrderDateRange,
    getOrderTimeRange,
};

export { orderUtils };
